import { useMutation, useQueryClient } from '@tanstack/react-query'
import { rqKeys } from '@/react-query-keys'
import { apiRequestNew } from '@/helpers/request'
import {
  readPendingArchiveQueue,
  removePendingArchiveQueueItem,
  upsertPendingArchiveQueueItem,
} from '@/helpers/certificate-archive-offline-queue'
import { removePendingRestoreQueueItem } from '@/helpers/certificate-restore-offline-queue'
import { useAppStore } from '@/store'
import type { T_CertificatesListRes } from '@/types/responses'

export const useArchiveCertificate = () => {
  const queryClient = useQueryClient()
  const userData = useAppStore((s) => s.userData)
  type T_CertificateItem = T_CertificatesListRes[number]
  const logArchiveMutation = (_phase: string, _payload: Record<string, unknown> = {}) => {
    void _phase
    void _payload
  }

  const applyOptimisticArchiveMove = (
    certificateIdToArchive: number,
    fallbackCert?: T_CertificateItem,
  ) => {
    logArchiveMutation('apply-optimistic:start', { certificateId: Number(certificateIdToArchive) })
    const previousCertificates = queryClient.getQueryData<T_CertificatesListRes>([ rqKeys.certificatesList ])
    const previousArchived = queryClient.getQueryData<T_CertificatesListRes>([ rqKeys.certificatesList, 'archived' ])
    const certificateToArchive = (
      previousCertificates?.find((c) => c.id === certificateIdToArchive)
      || fallbackCert
      || previousArchived?.find((c) => c.id === certificateIdToArchive)
      || readPendingArchiveQueue(userData.id).find((p) => p.cert.id === certificateIdToArchive)?.cert
    )

    if (certificateToArchive) {
      removePendingRestoreQueueItem(userData.id, Number(certificateIdToArchive))
      upsertPendingArchiveQueueItem(userData.id, {
        cert: certificateToArchive,
        movedAt: Date.now(),
      })

      queryClient.setQueryData<T_CertificatesListRes>(
        [ rqKeys.certificatesList ],
        (old) => old?.filter((c) => c.id !== certificateIdToArchive) || []
      )

      queryClient.setQueryData<T_CertificatesListRes>(
        [ rqKeys.certificatesList, 'archived' ],
        (old) => {
          const next = [ certificateToArchive, ...(old || []) ]
            .filter((c, i, arr) => arr.findIndex((x) => x.id === c.id) === i)
          return next
        }
      )
    }

    logArchiveMutation('apply-optimistic:done', {
      certificateId: Number(certificateIdToArchive),
      foundInActive: Boolean(certificateToArchive),
      usedFallback: Boolean(!previousCertificates?.find((c) => c.id === certificateIdToArchive) && fallbackCert),
      activeCountBefore: previousCertificates?.length || 0,
      archivedCountBefore: previousArchived?.length || 0,
    })
    return { previousCertificates, previousArchived }
  }

  const {
    mutate: moveCertificateToArchiveMutation,
    isPending: isMoveCertificateToArchivePending,
  } = useMutation({
    mutationFn: ({ certificateIdToArchive }: { certificateIdToArchive: number, fallbackCert?: T_CertificateItem }) => apiRequestNew<any>(
      'DELETE',
      `/era/gift/certificate/${certificateIdToArchive}`,
    ),
    onMutate: ({ certificateIdToArchive, fallbackCert }) => {
      logArchiveMutation('mutate', {
        certificateId: Number(certificateIdToArchive),
        fallbackProvided: Boolean(fallbackCert),
      })
      const context = applyOptimisticArchiveMove(certificateIdToArchive, fallbackCert)
      void queryClient.cancelQueries({ queryKey: [ rqKeys.certificatesList ] })
      void queryClient.cancelQueries({ queryKey: [ rqKeys.certificatesList, 'archived' ] })
      return context
    },
    onError: (err: any, vars, context: any) => {
      const certificateIdToArchive = vars?.certificateIdToArchive
      const isOfflineNow = typeof navigator !== 'undefined' && navigator.onLine === false
      const isNetworkError = isOfflineNow || err?.code === 'ERR_NETWORK' || !err?.response
      const isAlreadyArchived = Number(err?.response?.status) === 404
      logArchiveMutation('error', {
        certificateId: Number(certificateIdToArchive),
        code: err?.code,
        status: Number(err?.response?.status) || undefined,
        isNetworkError,
        isAlreadyArchived,
      })

      if (isNetworkError) {
        return
      }
      if (isAlreadyArchived) {
        removePendingArchiveQueueItem(userData.id, Number(certificateIdToArchive))
        return
      }

      removePendingArchiveQueueItem(userData.id, Number(certificateIdToArchive))
      if (context?.previousCertificates) {
        queryClient.setQueryData([ rqKeys.certificatesList ], context.previousCertificates)
      }
      if (context?.previousArchived) {
        queryClient.setQueryData([ rqKeys.certificatesList, 'archived' ], context.previousArchived)
      }
    },
    onSuccess: (_res, vars) => {
      const certificateIdToArchive = vars?.certificateIdToArchive
      logArchiveMutation('success', { certificateId: Number(certificateIdToArchive) })
    },
    onSettled: () => {
      logArchiveMutation('settled')
      // Mark stale, but avoid automatic refetch (we do our own polling+merge above).
      queryClient.invalidateQueries({ queryKey: [ rqKeys.certificatesList ], refetchType: 'none' })
      queryClient.invalidateQueries({ queryKey: [ rqKeys.certificatesList, 'archived' ], refetchType: 'none' })
    }
  })

  const archiveWithOfflineQueue = async (id: number, fallbackCert?: T_CertificateItem) => {
    const isOffline = typeof navigator !== 'undefined' && navigator.onLine === false
    logArchiveMutation('archiveWithOfflineQueue:start', {
      certificateId: Number(id),
      isOffline,
      fallbackProvided: Boolean(fallbackCert),
    })
    if (isOffline) {
      await queryClient.cancelQueries({ queryKey: [ rqKeys.certificatesList ] })
      await queryClient.cancelQueries({ queryKey: [ rqKeys.certificatesList, 'archived' ] })
      applyOptimisticArchiveMove(id, fallbackCert)
      logArchiveMutation('archiveWithOfflineQueue:offline-optimistic-applied', { certificateId: Number(id) })
      return
    }

    // Anti-race for instant page reload:
    // persist pending archive intent before online mutation dispatch,
    // so UI can still hide this certificate after reload.
    const certForPending = (
      fallbackCert
      || queryClient.getQueryData<T_CertificatesListRes>([ rqKeys.certificatesList ])?.find((c) => c.id === id)
      || queryClient.getQueryData<T_CertificatesListRes>([ rqKeys.certificatesList, 'archived' ])?.find((c) => c.id === id)
      || readPendingArchiveQueue(userData.id).find((p) => p.cert.id === id)?.cert
    )
    if (certForPending) {
      removePendingRestoreQueueItem(userData.id, Number(id))
      upsertPendingArchiveQueueItem(userData.id, {
        cert: certForPending,
        movedAt: Date.now(),
      })
    }

    moveCertificateToArchiveMutation({ certificateIdToArchive: id, fallbackCert })
    logArchiveMutation('archiveWithOfflineQueue:online-mutate-dispatched', { certificateId: Number(id) })
  }

  return {
    archiveWithOfflineQueue,
    isMoveCertificateToArchivePending,
  }
}
